import { computed, inject } from '@angular/core';
import { LangService } from '@td/i18n';
import { filter, pipe, skip, switchMap, tap } from 'rxjs';
import { type Incident } from '@td/core';
import { tapResponse } from '@ngrx/operators';
import { IncidentService } from './incident.service';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { setError, setLoaded, setLoading, withCallState, withReset } from '@ngrx-toolkit/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AuthStore } from '../../../../core';
import { toObservable } from '@angular/core/rxjs-interop';

type State = {
  incident: Incident | null;
};

const initialState: State = {
  incident: null,
};

export const IncidentStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCallState(),
  withReset(),
  withComputed(({ incident }) => ({
    courtDelay: computed(() => incident()?.courtDelay),
    departmentName: computed(() => incident()?.departmentName),
    isCaliforniaTraffic: computed(() => incident()?.departmentState === 'CA' && incident()?.workflowType === 'TRAFFIC'),
    isArmedForces: computed(() => incident()?.workflowType === 'ARMED_FORCES_TRAFFIC'),
    isFederal: computed(() => incident()?.workflowType === 'FEDERAL'),
    isCitation: computed(() => incident()?.violationDetails.some(({ type }) => type === 'CITATION')),
  })),
  withProps(({ incident }) => ({
    incident$: toObservable(incident),
  })),
  withMethods((store, api = inject(IncidentService), lang = inject(LangService)) => ({
    load: rxMethod<void>(
      pipe(
        tap(() => patchState(store, setLoading())),
        switchMap(() =>
          api.load(lang.language()).pipe(
            tapResponse({
              next: (incident) => patchState(store, { incident }, setLoaded()),
              error: (error) => patchState(store, setError(error)),
            }),
          ),
        ),
      ),
    ),
  })),
  withHooks({
    onInit(store, authStore = inject(AuthStore), lang = inject(LangService)) {
      lang.lang$
        .pipe(
          filter(() => !!store.incident()),
          skip(1),
        )
        .subscribe(() => store.load());
      authStore.logout$.subscribe(() => store.resetState());
    },
  }),
);
